import { useState, type FormEvent } from 'react';
import { clientsApi, type ClientPortalUser } from '@/api/client';
import { useAsync } from '@/hooks';
import { useAuth } from '@/auth/AuthContext';
import { PortalLayout } from '@/components/Layout';
import { PortalNav } from '@/components/Nav';
import { DataTable } from '@/components/DataTable';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { StatusBadge } from '@/components/StatusBadge';
import { useConfirm, useToast } from '@/components/Feedback';

const ROLE_OPTIONS = [
  { value: 'Client Main', label: 'Admin — books trips and manages users' },
  { value: 'Client Sub', label: 'Booker — books and tracks trips' },
  { value: 'Client Viewer', label: 'Viewer — tracks trips only' },
];

function roleName(value: string): string {
  return ROLE_OPTIONS.find((o) => o.value === value)?.label.split(' — ')[0] ?? value;
}

export function PortalUsers() {
  const { user, hasPermission } = useAuth();
  const clientId = user?.clientId;
  const canManage = hasPermission('portal.manage_users');
  const confirm = useConfirm();
  const toast = useToast();

  const { data: users, loading, error: loadError, reload } = useAsync(
    () => (clientId && canManage ? clientsApi.listUsers(clientId) : Promise.resolve([] as ClientPortalUser[])),
    [clientId, canManage],
  );

  const [showInvite, setShowInvite] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [clientRole, setClientRole] = useState('Client Sub');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [inviteError, setInviteError] = useState('');

  if (!canManage) {
    return (
      <PortalLayout>
        <div className="page">
          <div className="portal-nav">
            <PortalNav variant="top" />
          </div>
          <h1>Users</h1>
          <p className="text-muted">Only your company&apos;s portal admins can manage users. Ask one of them, or your account manager, for help.</p>
        </div>
        <style>{`.portal-nav { margin-bottom: var(--space-lg); }`}</style>
      </PortalLayout>
    );
  }

  const handleInvite = async (e: FormEvent) => {
    e.preventDefault();
    if (!clientId) return;
    setSaving(true);
    setInviteError('');
    try {
      await clientsApi.inviteUser(clientId, {
        email,
        password,
        clientRole,
        suppressDelivery: false,
      });
      setShowInvite(false);
      toast(`Invitation sent to ${email}.`);
      setEmail('');
      setPassword('');
      reload();
    } catch (err) {
      setInviteError((err as { message?: string }).message ?? 'We couldn\'t send the invitation. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleRoleChange = async (row: ClientPortalUser, nextRole: string) => {
    if (!clientId) return;
    setError('');
    try {
      await clientsApi.updateUser(clientId, row.userId, { clientRole: nextRole });
      toast(`${row.email} is now ${roleName(nextRole)}.`);
      reload();
    } catch (err) {
      setError((err as { message?: string }).message ?? `Couldn't change the role for ${row.email}. Please try again.`);
    }
  };

  const handleRemove = async (row: ClientPortalUser) => {
    if (!clientId) return;
    const ok = await confirm({
      title: 'Remove user?',
      message: `Remove ${row.email}? They will lose access immediately.`,
      confirmLabel: 'Remove user',
      cancelLabel: 'Keep user',
      danger: true,
    });
    if (!ok) return;
    setError('');
    try {
      await clientsApi.removeUser(clientId, row.userId);
      toast(`${row.email} removed.`);
      reload();
    } catch (err) {
      setError((err as { message?: string }).message ?? `Couldn't remove ${row.email}. Please try again.`);
    }
  };

  return (
    <PortalLayout>
      <div className="page">
        <div className="portal-nav">
          <PortalNav variant="top" />
        </div>

        <div className="page-header row-between">
          <div>
            <h1>Users</h1>
            <p className="text-muted">Invite colleagues and choose what they can do in the portal</p>
          </div>
          <Button
            type="button"
            onClick={() => {
              setInviteError('');
              setShowInvite(true);
            }}
          >
            Invite user
          </Button>
        </div>

        {error && <p className="form-error" role="alert">{error}</p>}

        <DataTable
          columns={[
            { key: 'email', header: 'Email' },
            {
              key: 'clientRole',
              header: 'Role',
              render: (row: ClientPortalUser) => {
                const isSelf = row.userId === user?.id;
                return (
                  <select
                    value={row.clientRole}
                    aria-label={`Role for ${row.email}`}
                    disabled={isSelf}
                    title={isSelf ? 'You can\'t change your own role' : undefined}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => handleRoleChange(row, e.target.value)}
                  >
                    {ROLE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                );
              },
            },
            {
              key: 'status',
              header: 'Status',
              render: (row: ClientPortalUser) => <StatusBadge status={row.status} size="sm" audience="client" />,
            },
            {
              key: 'actions',
              header: '',
              sortable: false,
              render: (row: ClientPortalUser) =>
                row.userId === user?.id ? null : (
                  <Button size="sm" variant="danger" type="button" onClick={() => handleRemove(row)}>
                    Remove
                  </Button>
                ),
            },
          ]}
          data={users ?? []}
          keyField="id"
          loading={loading}
          dense
          loadError={loadError}
          emptyMessage="No other users yet. Invite a colleague to give them access."
        />

        <Modal open={showInvite} onClose={() => setShowInvite(false)} title="Invite user">
          <form onSubmit={handleInvite}>
            <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            <Input
              label="Temporary password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={10}
              hint="At least 10 characters. Share it with them so they can sign in the first time."
            />
            <Select
              label="Role"
              value={clientRole}
              onChange={(e) => setClientRole(e.target.value)}
            >
              {ROLE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Select>
            {inviteError && <p className="form-error" role="alert">{inviteError}</p>}
            <Button type="submit" loading={saving} fullWidth>
              Send invitation
            </Button>
          </form>
        </Modal>
      </div>
      <style>{`
        .portal-nav { margin-bottom: var(--space-lg); }
        .form-error {
          background: #FDE8E8;
          color: var(--color-urgent);
          padding: var(--space-sm) var(--space-md);
          border-radius: var(--radius-sm);
          font-size: 0.875rem;
          margin-bottom: var(--space-md);
        }
      `}</style>
    </PortalLayout>
  );
}
